/* eslint-disable max-lines-per-function */
import { Ref } from 'react';

import { VisitDiagnosis } from '@dreryk/egabinet-visit-frontend-api';
import { Address } from '@dreryk/address-common-api';

import { assertUnreachable, createInterpolatedTranslationKey } from '@dreryk/react-common';
import { Scroll, StyledFlex } from '@dreryk/react-components';

import { RecipientCategory, UnsentPrescription } from '~libs.shared.prescription';

import { draftPrescriptionsTranslationKeys } from '../../../../translations';
import { PatientId, VisitId } from '../../PrescriptionIssuingPanel.types';
import { PrescriptionListHeader } from '../prescription/PrescriptionListHeader/PrescriptionListHeader';

import { useDraftPrescriptionsFieldArray, useDraftPrescriptionsFormController } from './DraftPrescriptionsForm.hooks';
import { DraftPrescriptionsFieldArrayMethods, DraftPrescriptionsFormController } from './DraftPrescriptionsForm.types';
import { DraftPrescriptionsFormPlaceholder } from './DraftPrescriptionsFormPlaceholder';
import { DraftPrescriptionsStatusPanel } from './DraftPrescriptionsStatusPanel';
import { DraftMagistralDrugPrescriptionField } from './magistralDrugPrescription/DraftMagistralDrugPrescriptionField';
import { DraftMagistralDrugPrescriptionFieldLoader } from './magistralDrugPrescription/DraftMagistralDrugPrescriptionField.loader';
import { DraftMagistralDrugPrescription } from './magistralDrugPrescription/DraftMagistralDrugPrescriptionField.types';
import { PatientEntitlementsLink } from './patientEntitlements/PatientEntitlementsLink';
import { PrescriptionCategoryField } from './prescriptionCategory/PrescriptionCategoryField';
import { DraftStandardPrescriptionField } from './standardPrescription/DraftStandardPrescriptionField';
import { DraftStandardPrescriptionFieldLoader } from './standardPrescription/DraftStandardPrescriptionField.loader';
import { DraftStandardPrescription } from './standardPrescription/DraftStandardPrescriptionField.types';
import { PatientAddressIncompleteStatus } from './patientAddress/PatientAddressIncompleteStatus';
import { isPatientAddressForPrescriptionIncomplete } from './patientAddress/PatientAddressIncomplete.utils';

type DraftPrescriptionsFormChildren = (
   renderProps: Pick<
      DraftPrescriptionsFormController,
      | 'control'
      | 'formState'
      | 'handleSubmitDraft'
      | 'handleSubmit'
      | 'trigger'
      | 'watch'
      | 'markDirty'
      | 'markPristine'
   >
) => JSX.Element;

type DraftPrescriptionsFormProps = {
   readonly visitId: VisitId;
   readonly patientId: PatientId;
   readonly patientAddress: Address | undefined;
   readonly unsentPrescriptions: UnsentPrescription[];
   readonly initialRecipientCategory: RecipientCategory;
   readonly draftPrescriptionsFieldArrayMethodsRef: Ref<DraftPrescriptionsFieldArrayMethods>;
   readonly children: DraftPrescriptionsFormChildren;
   readonly visitDiagnoses: VisitDiagnosis[];
   readonly inPersonExaminationPerformed: boolean;
};

// eslint-disable-next-line max-lines-per-function
export const DraftPrescriptionsForm = (props: DraftPrescriptionsFormProps): JSX.Element => {
   const {
      control,
      register,
      watch,
      setValue,
      getValue,
      formState,
      handleSubmit,
      trigger,
      handleSubmitDraft,
      setError,
      markDirty,
      markPristine,
   } = useDraftPrescriptionsFormController(props.unsentPrescriptions, props.initialRecipientCategory, props.patientId);
   const { fields: draftPrescriptionFields, remove: removePrescription } = useDraftPrescriptionsFieldArray(
      control,
      props.draftPrescriptionsFieldArrayMethodsRef
   );

   const shouldDisplayIncompletePatientAddressWarning =
      draftPrescriptionFields.length !== 0 &&
      (!props.patientAddress || isPatientAddressForPrescriptionIncomplete(props.patientAddress));

   return (
      <StyledFlex direction={'column'} gap={'4px'}>
         <PrescriptionListHeader
            testId={'draftPrescriptions'}
            label={createInterpolatedTranslationKey(draftPrescriptionsTranslationKeys.placePrescription, {
               count: draftPrescriptionFields.length,
            })}
         >
            {draftPrescriptionFields.length === 0 ? null : <PrescriptionCategoryField register={register} />}
            <PatientEntitlementsLink control={control} patientId={props.patientId} />
            {shouldDisplayIncompletePatientAddressWarning ? (
               <PatientAddressIncompleteStatus patientAddress={props.patientAddress} patientId={props.patientId} />
            ) : null}
         </PrescriptionListHeader>
         <DraftPrescriptionsStatusPanel />
         <Scroll testId={'draftPrescriptions'}>
            {draftPrescriptionFields.length ? (
               <StyledFlex direction={'column'} gap={'14px'}>
                  {draftPrescriptionFields.map((field, index) => {
                     const { type } = field;
                     const handleRemovePrescription = (): void => removePrescription(index);

                     switch (type) {
                        case 'standard': {
                           const draftStandardPrescription = field as DraftStandardPrescription;
                           return (
                              <DraftStandardPrescriptionFieldLoader
                                 key={field.id}
                                 index={index}
                                 medicineId={draftStandardPrescription.queryData.medicineId}
                                 patientId={draftStandardPrescription.queryData.patientId}
                                 medicinePackageQuantity={draftStandardPrescription.initialData.quantity}
                                 initialData={draftStandardPrescription.initialData.medicinePrescription}
                                 patientMedicine={draftStandardPrescription.selectedByUser.patientMedicine}
                                 visitDiagnoses={props.visitDiagnoses}
                                 visitId={props.visitId}
                              >
                                 {({ medicinePrescription }) => (
                                    <DraftStandardPrescriptionField
                                       {...draftStandardPrescription}
                                       index={index}
                                       register={register}
                                       watch={watch}
                                       setError={setError}
                                       onRemove={handleRemovePrescription}
                                       setValue={setValue}
                                       getValue={getValue}
                                       trigger={trigger}
                                       formState={formState}
                                       medicinePrescription={medicinePrescription}
                                       visitDiagnoses={props.visitDiagnoses}
                                       inPersonExaminationPerformed={props.inPersonExaminationPerformed}
                                    />
                                 )}
                              </DraftStandardPrescriptionFieldLoader>
                           );
                        }
                        case 'magistralDrug': {
                           const draftMagistralDrugPrescription = field as DraftMagistralDrugPrescription;
                           return (
                              <DraftMagistralDrugPrescriptionFieldLoader
                                 key={field.id}
                                 index={index}
                                 visitId={props.visitId}
                                 magistralDrugId={draftMagistralDrugPrescription.queryData.magistralDrugId}
                                 patientId={draftMagistralDrugPrescription.queryData.patientId}
                                 medicinePackageQuantity={draftMagistralDrugPrescription.initialData.quantity}
                                 initialData={draftMagistralDrugPrescription.initialData.medicinePrescription}
                                 magistralDrug={draftMagistralDrugPrescription.selectedByUser.magistralDrug}
                              >
                                 {({ medicinePrescription, medicinePrescriptionFetching }) => (
                                    <DraftMagistralDrugPrescriptionField
                                       {...draftMagistralDrugPrescription}
                                       index={index}
                                       register={register}
                                       trigger={trigger}
                                       onRemove={handleRemovePrescription}
                                       setValue={setValue}
                                       watch={watch}
                                       medicinePrescription={medicinePrescription}
                                       isFetching={medicinePrescriptionFetching}
                                       visitDiagnoses={props.visitDiagnoses}
                                    />
                                 )}
                              </DraftMagistralDrugPrescriptionFieldLoader>
                           );
                        }
                        case undefined: {
                           return null;
                        }
                        default:
                           return assertUnreachable(type);
                     }
                  })}
               </StyledFlex>
            ) : (
               <DraftPrescriptionsFormPlaceholder />
            )}
         </Scroll>

         {props.children({
            control: control,
            formState: formState,
            handleSubmitDraft: handleSubmitDraft,
            handleSubmit: handleSubmit,
            trigger: trigger,
            watch: watch,
            markDirty: markDirty,
            markPristine: markPristine,
         })}
      </StyledFlex>
   );
};
